// Credit note- account

import dayjs, { Dayjs } from 'dayjs';
import { FormikErrors, FormikHandlers, FormikHelpers, FormikValues, useFormik } from 'formik';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import {
  Box,
  Checkbox,
  Grid,
  Stack,
  TableBody,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/system';
import { DataGrid, GridColDef, GridRowSelectionModel, GridValueGetterParams } from '@mui/x-data-grid';

import Accord from '@Components/Accordion';
import SbButton from '@Components/Button';
import DatePicker from '@Components/DatePicker';
import DateRangePicker from '@Components/DateRangePicker';
import FilterSelect from '@Components/Filter/Dropdown';
import FilterSearch from '@Components/Filter/Search';
import Image from '@Components/Image';
import Loader from '@Components/Loader';
import { SBTable } from '@Components/Table/Table';
import { TableHeadCell, TableRowCell } from '@Components/Table/TableCell';
import { SBTableRow } from '@Components/Table/TableRow';
import SbTextField from '@Components/Textfield';
import Title from '@Components/Title';

import { SHIPMENT_STATUS } from '@Constants/Shipment';
import { getShipmentWebsocketStream } from '@Reducers/AdminShipment/actions';
import { ShipmentObj } from '@Reducers/Invoice/Type';
import { InvoiceCreditDebitPayload } from '@Reducers/InvoiceList/Type';
import { getAccountUnbilledCreditList } from '@Reducers/InvoiceList/actions';
import { SuccessErrorModalOpen } from '@Reducers/SuccessErrorModal';
import { getSystemList } from '@Reducers/Systems';
import { sendAccountCreditDataTo365 } from '@Reducers/d365';
import API from '@Services/apiAxios';
import { AppDispatch, RootStateType } from '@Store';

import { formatNumber, useLocale } from '../../../utils/formatNumber';
import { AccountHolderInvoiceTableProps, AccountHolderUnbilledData, FilterObj } from '../Type';
import CreditDebitNoteModal from './components/CreditDebitNoteModal';
import {
  CreditDebitConfig,
  CreditDebitFilterType,
  SelectedCreditDebitParamType,
  buildCreditDebitSubmissionPayload,
  getCreditDebitGroupKey,
  getShipmentExtraChargeSum,
  updateSelectedCreditDebitRowsList,
  validateCreditDebitConfig,
} from './utils/creditDebitNoteUtils';

type selectedInvoices = { [x: string]: boolean };

type StepThreeProps = {
  selectedAccountRows: SelectedCreditDebitParamType[];
  selectedInvoices: selectedInvoices;
  list: AccountHolderUnbilledData[];
  values: FormikValues;
  handleChange: FormikHandlers['handleChange'];
  setFieldValue: FormikHelpers<InvoiceCreditDebitPayload>['setFieldValue'];
  errors?: FormikErrors<InvoiceCreditDebitPayload>;
  onInvoiceDateChange: (date: Dayjs) => void;
};

type StepTwoProps = {
  selectedInvoices: selectedInvoices;
  setSelectedUnbilledShipment: (obj: SelectedCreditDebitParamType) => void;
  list: AccountHolderUnbilledData[];
  selectedUnbilledRow: SelectedCreditDebitParamType[];
};

type AccordionTableProps = {
  data: AccountHolderUnbilledData;
  setSelectedUnbilledShipment: (obj: SelectedCreditDebitParamType) => void;
  selectedUnbilledRow: SelectedCreditDebitParamType[];
};

// Define the Invoice schema
const InvoiceSchema = yup.object().shape({
  invoiceNo: yup.mixed().required('Invoice number is required'),
  shipmentIds: yup.array().of(yup.string()).required('Shipment IDs are required'),
});

// Define the InvoiceData schema
const InvoiceDataSchema = yup.object().shape({
  invoice: yup.array().of(InvoiceSchema).required('Invoice details are required'),
  invoiceDate: yup.string().required('Invoice date is required'),
  remarks: yup.string(),
});

const initialState: InvoiceCreditDebitPayload = {
  invoice: [],
  invoiceDate: '',
  remarks: '',
};

const SearchFilterList = [
  { label: 'Account No.', value: 'account_no' },
  { label: 'Invoice Number', value: 'invoice_no' },
];

const TableActionContainer = styled('div')(({ theme }) => ({
  padding: '32px 0px',
  display: 'grid',
  gridTemplateColumns: '390px 320px 1fr 140px',
  columnGap: '15px',
  [theme.breakpoints.down('xl')]: {
    padding: '32px 0px',
    columnGap: '10px',
    gridTemplateColumns: '390px 320px 1fr 140px',
  },
  [theme.breakpoints.down('lg')]: {
    columnGap: '10px',
    gridTemplateColumns: '320px 1fr',
  },
  [theme.breakpoints.down('md')]: {
    columnGap: '5px',
    gridTemplateColumns: '1fr',
    rowGap: '15px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '16px 0px',
    gridTemplateColumns: '1fr',
    rowGap: '10px',
  },
}));

function InvoiceTable(props: AccountHolderInvoiceTableProps) {
  const { list = [], selectedInvoices, checkAllInvoices, onSelectInvoice } = props;
  const locale = useLocale();

  return (
    <SBTable>
      <TableHead>
        <TableRow>
          <TableHeadCell>
            <Checkbox
              style={{ color: 'inherit' }}
              checked={
                list?.length > 0 &&
                (selectedInvoices ? Object.keys(selectedInvoices).length : 0) === (list ? list.length : 0)
              }
              onChange={checkAllInvoices}
            />
          </TableHeadCell>
          <TableHeadCell>Invoice No.</TableHeadCell>
          <TableHeadCell>Account No.</TableHeadCell>
          <TableHeadCell>Bill To</TableHeadCell>
          <TableHeadCell>No of Shipments</TableHeadCell>
          <TableHeadCell>System</TableHeadCell>
          <TableHeadCell>Total Amount</TableHeadCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {list.length ? (
          list.map((data, i) => (
            <SBTableRow key={getCreditDebitGroupKey(data) || i} className={selectedInvoices[i] ? 'selected' : ''}>
              <TableRowCell align="center">
                <div>
                  <Checkbox
                    style={{ color: 'inherit' }}
                    onChange={e => onSelectInvoice(i, e.target.checked)}
                    checked={!!selectedInvoices[i]}
                  />
                </div>
              </TableRowCell>
              <TableRowCell align="center" className="md-text uppercase">
                {data.invoiceNumber}
              </TableRowCell>
              <TableRowCell align="center" className="md-text uppercase">
                {data.account?.accountNumber || data.accountNumber}
              </TableRowCell>
              <TableRowCell align="center" className="md-text capitalize">
                <p>{data?.company ? data?.company?.name : data?.user ? data?.user?.name : '-'}</p>{' '}
                <p className="text-[#AFAFAF]">
                  {data?.company ? data?.company?.email : data?.user ? data?.user?.email : '-'}
                </p>
              </TableRowCell>
              <TableRowCell
                sx={{
                  div: {
                    textAlign: 'center',
                  },
                }}
                align="center"
                className="md-text"
              >
                {data.shipments.length}
              </TableRowCell>
              <TableRowCell align="center" className="md-text">
                {data?.system?.name || (typeof data?._systemId === 'object' ? data?._systemId?.name : '') || '-'}
              </TableRowCell>
              <TableRowCell
                sx={{
                  div: {
                    textAlign: 'center',
                  },
                }}
                align="center"
                className="md-text"
              >
                {data?.totalInvoiceAmount !== undefined && formatNumber(data.totalInvoiceAmount, locale)}
              </TableRowCell>
            </SBTableRow>
          ))
        ) : (
          <SBTableRow>
            <TableRowCell colSpan={7}>
              <Typography className="sb-text-gray font-medium text-center">No Data Found.</Typography>
            </TableRowCell>
          </SBTableRow>
        )}
      </TableBody>
    </SBTable>
  );
}

export default function InvoiceList() {
  const [filterSelectValue, setFilterSelectValue] = useState('account_no');
  const [filterText, setFilterText] = useState('');
  const [step, setStep] = useState(1);
  const [system, setSystem] = useState<string[]>([]);
  const [invoiceDate, setInvoiceDate] = useState<string>('');
  const [dateRange, setDateRange] = useState({
    startDate: moment().subtract(1, 'months').startOf('day').toDate(),
    endDate: moment().endOf('day').toDate(),
  });

  const [selectedInvoices, setSelectedInvoices] = useState<selectedInvoices>({});
  const [selectedAccountRows, setSelectedAccountRows] = useState<SelectedCreditDebitParamType[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const SystemList = useSelector((state: RootStateType) => state.System.data.selectedList).map(sys => ({
    display: sys.name,
    value: sys._id,
  }));
  const _systems = useSelector((state: RootStateType) => state.loginUser?.profile?._systems);
  const activeSystemList = useMemo(() => {
    if (_systems?.length) return SystemList.filter(sys => _systems.includes(sys.value));
    else return [];
  }, [SystemList, _systems]);

  const {
    unBilledList: { list = [], currentPage = 1, perPage = 10, totalCount = 0 },
  } = useSelector((state: RootStateType) => state.InvoiceList);

  useEffect(() => {
    const fromDate = moment(dateRange.startDate).format('DD/MM/YYYY');
    const toDate = moment(dateRange.endDate).format('DD/MM/YYYY');
    dispatch(getAccountUnbilledCreditList({ currentPage: 1, perPage: 10, fromDate, toDate }));
    dispatch(getSystemList());
  }, []);

  const getFilterObj = useCallback(
    (options: FilterObj): CreditDebitFilterType => {
      setSelectedInvoices({});
      const filterDate = options.dateRange || dateRange;
      const fromDate = moment(filterDate.startDate).format('DD/MM/YYYY');
      const toDate = moment(filterDate.endDate).format('DD/MM/YYYY');
      const filterSystem = options.system || system;
      const filterSearchKey = filterSelectValue;
      const filterSearchValue = 'searchValue' in options ? options.searchValue : filterText;
      const filter: CreditDebitFilterType = {
        currentPage: options.currentPage || currentPage,
        perPage: options.perPage || perPage,
        fromDate,
        toDate,
        systems: filterSystem,
      };

      if (filterSearchValue) {
        filter.searchKey = filterSearchKey;
        filter.searchValue = filterSearchValue;
      }

      return filter;
    },
    [dateRange, system, filterSelectValue, filterText, currentPage, perPage],
  );

  const onDateChange = ({ startDate, endDate }: { startDate: Date; endDate: Date }, isOpen: boolean) => {
    if (isOpen) return;
    const nextDateRange = { startDate, endDate };
    setDateRange(nextDateRange);
    dispatch(getAccountUnbilledCreditList(getFilterObj({ dateRange: nextDateRange })));
  };

  const onSystemFilter = (selected: Array<string>) => {
    setSystem(selected);
    dispatch(getAccountUnbilledCreditList(getFilterObj({ system: selected })));
  };

  const onSelectInvoice = function (invoiceId: number, isChecked: boolean) {
    const newSelectedInvoice = { ...selectedInvoices };
    if (isChecked) newSelectedInvoice[invoiceId] = true;
    else delete newSelectedInvoice[invoiceId];
    setSelectedInvoices(newSelectedInvoice);
  };

  const checkAllInvoices = function (event: React.ChangeEvent<HTMLInputElement>) {
    if (event.target.checked) {
      const newInvoiceCheckedList = list.reduce((op: { [k: string]: boolean }, _, index) => {
        op[index] = true;
        return op;
      }, {});
      setSelectedInvoices(newInvoiceCheckedList);
    } else {
      setSelectedInvoices({});
    }
  };

  const setSelectedUnbilledShipment = useCallback((_params: SelectedCreditDebitParamType) => {
    setSelectedAccountRows(prev => updateSelectedCreditDebitRowsList(prev, _params));
  }, []);

  const nextStep = () => {
    if (step === 3) return;
    setStep(step + 1);
  };

  const preStep = () => {
    if (step <= 0) return;
    setStep(step - 1);
    if (step === 2) {
      setSelectedAccountRows([]);
    }
  };

  const isPrevDisabled = step <= 1;

  function convertDateFormat(dateStr: string): string {
    if (!dateStr) return '';
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const [day, month, year] = parts;
      return `${year}-${month}-${day}`;
    }
    return dateStr;
  }

  const submitCreditNoteCreation = useCallback(
    (config: CreditDebitConfig, accountRows: SelectedCreditDebitParamType[], onSuccess?: () => void) => {
      const missing = validateCreditDebitConfig(config, accountRows, 'credit');
      if (missing.length > 0) {
        dispatch(
          SuccessErrorModalOpen({
            type: 'error',
            title: 'Validation Error',
            subTitle: `Please complete all required fields before proceeding. ${missing.join(', ')}`,
          }),
        );
        return;
      }

      const customPayload = buildCreditDebitSubmissionPayload(config, accountRows, 'credit');
      setIsSubmitting(true);
      API.post('/invoice/creditnote', customPayload)
        .then(res => {
          dispatch(
            SuccessErrorModalOpen({
              type: 'success',
              title: 'Success',
              subTitle: res?.data?.msg || 'Credit note created successfully.',
            }),
          );
          const accountData = accountRows.map(item => ({
            _shipmentNumbers: item.shipments.map(s => s.shipmentNumber),
          }));
          const dataForStream = accountData.flatMap(el => el._shipmentNumbers);
          if (dataForStream.length > 0) {
            dispatch(getShipmentWebsocketStream(dataForStream.map(num => `shipment-update-${num}`)));
          }
          if (onSuccess) onSuccess();
          navigate('/invoice/view');
        })
        .catch(error => {
          dispatch(
            SuccessErrorModalOpen({
              type: 'error',
              title: 'Oops!',
              subTitle: error?.response?.data?.msg || 'Something went wrong.',
            }),
          );
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    },
    [dispatch, navigate],
  );

  const { handleSubmit, ...formik } = useFormik<InvoiceCreditDebitPayload>({
    initialValues: {
      ...initialState,
      invoice: selectedAccountRows?.map(row => ({
        invoiceNo: Number(row?.invoiceNumber) || 0,
        shipmentIds: row?.shipments?.map((item: ShipmentObj) => item?._id),
      })),
    },
    enableReinitialize: true,
    validateOnChange: false,
    onSubmit: values => {
      const config: CreditDebitConfig = {
        invoiceDate: invoiceDate
          ? dayjs(convertDateFormat(invoiceDate))
          : values.invoiceDate
          ? dayjs(values.invoiceDate)
          : null,
        dueDate: invoiceDate
          ? dayjs(convertDateFormat(invoiceDate))
          : values.invoiceDate
          ? dayjs(values.invoiceDate)
          : null,
        remarks: formik?.values?.remarks || '',
      };
      submitCreditNoteCreation(config, selectedAccountRows);
    },
    validationSchema: InvoiceDataSchema,
  });

  const stepThreeMissingFields = useMemo(() => {
    if (step !== 3) return [];
    const config: CreditDebitConfig = {
      invoiceDate: formik.values.invoiceDate
        ? dayjs(formik.values.invoiceDate)
        : invoiceDate
        ? dayjs(convertDateFormat(invoiceDate))
        : null,
      dueDate: formik.values.invoiceDate
        ? dayjs(formik.values.invoiceDate)
        : invoiceDate
        ? dayjs(convertDateFormat(invoiceDate))
        : null,
      remarks: formik.values.remarks || '',
    };
    return validateCreditDebitConfig(config, selectedAccountRows, 'credit');
  }, [step, formik.values.invoiceDate, formik.values.remarks, invoiceDate, selectedAccountRows]);

  const isNextDisabled =
    isSubmitting ||
    (step === 1 && Object.keys(selectedInvoices).length === 0) ||
    (step === 2 && selectedAccountRows.length === 0) ||
    (step === 3 && stepThreeMissingFields.length > 0);

  const handleInvoiceDateChange = (e: Dayjs) => {
    const date = e.toDate();
    setInvoiceDate(moment(date).format('DD/MM/YYYY'));
  };

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    if (isSubmitting) return;
    setIsModalOpen(false);
  }, [isSubmitting]);

  const handleRestorePageFilters = useCallback(() => {
    dispatch(getAccountUnbilledCreditList(getFilterObj({})));
  }, [dispatch, getFilterObj]);

  return (
    <>
      <Loader loading={isSubmitting} />
      <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <Box
          onClick={() => {
            if (step === 1) {
              navigate('/invoice/view');
            } else if (step === 2) {
              setStep(1);
            } else {
              setStep(2);
            }
          }}
          sx={{ zIndex: '1', cursor: 'pointer' }}
        >
          <Image src="/images/backbutton.svg" alt="Back Button" style={{ width: '1.5rem', marginTop: '0.2rem' }} />
        </Box>
        <Title title="Invoices" subTitle="View and create invoices" />
      </Box>

      <Box
        className="flex"
        sx={{
          marginTop: '52px',
          marginBottom: '25px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem',
        }}
      >
        <Box>
          <Title removeBorder removePadding title="Create Credit Note" titleClass="underline px_20-text" />
        </Box>
        <SbButton
          variant="contained"
          className="rounded-full"
          startIcon={<ReceiptLongOutlinedIcon fontSize="small" />}
          sx={{
            borderRadius: '10px !important',
            padding: '8px 20px !important',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
          onClick={handleOpenModal}
        >
          Quick Credit Note
        </SbButton>
      </Box>

      {step === 1 && (
        <>
          <TableActionContainer>
            <FilterSearch
              onSelectChange={(value: string) => {
                setFilterSelectValue(value);
              }}
              list={SearchFilterList}
              selectValue={filterSelectValue}
              textValue={filterText}
              onTextChange={e => {
                setFilterText(e.target.value);
              }}
              onTextSearch={() => {
                dispatch(getAccountUnbilledCreditList(getFilterObj({})));
              }}
              onTextClear={() => {
                setFilterText('');
                dispatch(getAccountUnbilledCreditList(getFilterObj({ searchValue: undefined })));
              }}
            />
            <DateRangePicker onChange={onDateChange} value={dateRange} />
            <div></div>
            <FilterSelect
              id="system"
              label="System"
              value={system}
              options={activeSystemList}
              onChange={onSystemFilter}
            />
          </TableActionContainer>
          <div
            style={{
              display: 'block',
              width: '100%',
              overflowX: 'auto',
              overflowY: 'hidden',
              maxWidth: '100%',
            }}
          >
            <div style={{ minWidth: '600px' }}>
              <InvoiceTable
                checkAllInvoices={checkAllInvoices}
                onSelectInvoice={onSelectInvoice}
                selectedInvoices={selectedInvoices}
                list={list}
              />
            </div>
            {totalCount ? (
              <TablePagination
                component="div"
                count={totalCount}
                page={currentPage - 1}
                rowsPerPage={perPage}
                onPageChange={(_, page) => {
                  dispatch(getAccountUnbilledCreditList(getFilterObj({ currentPage: page + 1 })));
                }}
                onRowsPerPageChange={event => {
                  dispatch(getAccountUnbilledCreditList(getFilterObj({ perPage: parseInt(event.target.value, 10) })));
                }}
              />
            ) : (
              ''
            )}
          </div>
        </>
      )}

      {step === 2 && (
        <StepTwo
          selectedInvoices={selectedInvoices}
          setSelectedUnbilledShipment={setSelectedUnbilledShipment}
          list={list}
          selectedUnbilledRow={selectedAccountRows}
        />
      )}

      {step === 3 && (
        <StepThree
          selectedInvoices={selectedInvoices}
          list={list}
          selectedAccountRows={selectedAccountRows}
          {...formik}
          onInvoiceDateChange={handleInvoiceDateChange}
        />
      )}

      <Grid container item mt={2} justifyContent="flex-end" spacing={4}>
        <Grid item xs={12} sm={3} md={2}>
          <SbButton variant="outlined" fullWidth className="rounded-full" onClick={preStep} disabled={isPrevDisabled}>
            Back
          </SbButton>
        </Grid>
        <Grid item xs={12} sm={3} md={2}>
          <Tooltip
            title={
              step === 3 && stepThreeMissingFields.length > 0 ? (
                <Box sx={{ p: 0.5, maxWidth: 300 }}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, mb: 0.5, color: 'inherit' }}>
                    Cannot create credit note. Required field(s) missing:
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2, fontSize: '0.75rem', lineHeight: 1.5 }}>
                    {stepThreeMissingFields.map(field => (
                      <li key={field}>{field}</li>
                    ))}
                  </Box>
                </Box>
              ) : (
                ''
              )
            }
            arrow
            enterDelay={150}
            placement="top"
            disableHoverListener={!(step === 3 && stepThreeMissingFields.length > 0)}
          >
            <span style={{ display: 'block', width: '100%' }}>
              <SbButton
                variant="contained"
                fullWidth
                className="rounded-full"
                onClick={
                  step === 3
                    ? () => {
                        handleSubmit();
                      }
                    : nextStep
                }
                disabled={isNextDisabled}
                loading={step === 3 && isSubmitting}
              >
                {step === 3 ? 'Create' : 'Next'}
              </SbButton>
            </span>
          </Tooltip>
        </Grid>
      </Grid>

      <CreditDebitNoteModal
        open={isModalOpen}
        onClose={handleCloseModal}
        noteType="credit"
        activeSystemList={activeSystemList}
        isSubmitting={isSubmitting}
        onSubmitNote={submitCreditNoteCreation}
        initialFilterSelectValue={filterSelectValue}
        initialFilterText={filterText}
        initialDateRange={dateRange}
        initialSystem={system}
        onRestorePageFilters={handleRestorePageFilters}
      />
    </>
  );
}

function StepTwo(props: StepTwoProps) {
  const { selectedInvoices, setSelectedUnbilledShipment, selectedUnbilledRow, list } = props;
  const selectedShipmentsData = useMemo(
    () =>
      Object.keys(selectedInvoices)
        .map((i: string) => list[Number(i)])
        .filter(Boolean),
    [selectedInvoices, list],
  );

  return (
    <>
      {selectedShipmentsData?.map(s => (
        <AccordionTable
          key={s?.invoiceNumber || getCreditDebitGroupKey(s)}
          data={s}
          setSelectedUnbilledShipment={setSelectedUnbilledShipment}
          selectedUnbilledRow={selectedUnbilledRow?.filter(
            item => String(item?.invoiceNumber) === String(s?.invoiceNumber),
          )}
        />
      ))}
    </>
  );
}

function StepThree(props: StepThreeProps) {
  const [isWyngsMy, setIsWyngsMy] = useState<boolean>(false);

  const { handleChange, errors, values, selectedAccountRows, setFieldValue, onInvoiceDateChange } = props;
  const { loading: loadingD365 } = useSelector((state: RootStateType) => state?.D365);
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    const hostname = window.location.hostname;
    const subdomain = hostname.split('.')[0];
    setIsWyngsMy(subdomain === 'wyngs-my');
  }, []);

  const sendTo365 = () => {
    const accountData = selectedAccountRows.map(item => ({
      _userId: item?._userId,
      accountNo: item.accountNumber,
      _systemId: item._systemId,
      _shipmentIds: item.shipments.map(s => s._id),
      _shipmentNumbers: item.shipments.map(s => s.shipmentNumber),
    }));
    dispatch(
      sendAccountCreditDataTo365({
        accountData,
        dueDate: dayjs(values.invoiceDate).format('YYYY-MM-DD'),
        invoiceDate: dayjs(values.invoiceDate).format('YYYY-MM-DD'),
        remarks: values?.remarks,
      }),
    );
  };

  return (
    <>
      <Loader loading={loadingD365} />
      <Grid container gap={2} sx={{ mb: 2 }}>
        <Grid item md={5} mt={'3.8rem'}>
          <Grid container gap={3}>
            <Grid item xs={11}>
              <DatePicker
                label="Invoice Date"
                value={values.invoiceDate}
                handleChange={(date: Dayjs) => {
                  onInvoiceDateChange(date);
                  setFieldValue('invoiceDate', date);
                }}
                required
                error={errors?.invoiceDate}
              />
            </Grid>
          </Grid>
          <Grid container mt={3}>
            <Grid item md={11}>
              <SbTextField
                label="Remark"
                name="remarks"
                multiline
                rows={4}
                value={values.remarks}
                onChange={handleChange}
                error={errors?.remarks}
              />
            </Grid>
            <Grid item md={11} mt={3}>
              <div style={{ display: 'flex', marginTop: '10px' }}>
                {!isWyngsMy && (
                  <SbButton
                    className="rounded-full"
                    variant="contained"
                    sx={{ borderRadius: '8px !important', padding: '8px !important' }}
                    onClick={sendTo365}
                    disabled={!values.invoiceDate || selectedAccountRows.length === 0}
                  >
                    Send to D365
                  </SbButton>
                )}
              </div>
            </Grid>
          </Grid>
        </Grid>
        <Grid item md={6.8}>
          <Grid container justifyContent={'space-between'} alignItems={'center'} my={'1rem'}>
            <Grid item xs="auto">
              <Box pb={'0.5rem'} borderBottom={'4px solid #080808'} width={'170px'}>
                <Title removePadding removeBorder title="Preview Credit Notes" titleClass=" px_16-text" />
              </Box>
            </Grid>
            <Grid item xs="auto" justifyContent={'flex-end'} container>
              <Title
                removePadding
                removeBorder
                title={'No. of Credit Notes : ' + selectedAccountRows?.length}
                titleClass=" px_16-text"
              />
            </Grid>
          </Grid>
          <Box sx={{ mb: 3 }}>
            <SBTable>
              <TableHead>
                <TableRow>
                  <TableHeadCell>Sr. No.</TableHeadCell>
                  <TableHeadCell>Invoice No.</TableHeadCell>
                  <TableHeadCell>System</TableHeadCell>
                  <TableHeadCell>No of Shipments</TableHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedAccountRows.map((data, index) => (
                  <SBTableRow key={getCreditDebitGroupKey(data) || index}>
                    <TableRowCell
                      sx={{
                        div: {
                          textAlign: 'center',
                        },
                      }}
                      align="center"
                    >
                      {index + 1}
                    </TableRowCell>
                    <TableRowCell align="center" className="md-text uppercase">
                      {data.invoiceNumber}
                    </TableRowCell>
                    <TableRowCell align="center" className="md-text capitalize">
                      <p>{data.systemName}</p>
                    </TableRowCell>
                    <TableRowCell
                      sx={{
                        div: {
                          textAlign: 'center',
                        },
                      }}
                      align="center"
                      className="md-text"
                    >
                      {data.shipments?.length}
                    </TableRowCell>
                  </SBTableRow>
                ))}
              </TableBody>
            </SBTable>
          </Box>
        </Grid>
      </Grid>
    </>
  );
}

function AccordionTable(props: AccordionTableProps) {
  const {
    data: { shipments = [], system, totalInvoiceAmount = 0, account, user, invoiceNumber = 0, company, _systemId },
    setSelectedUnbilledShipment,
    selectedUnbilledRow,
  } = props;

  const locale = useLocale();
  const orgCountryCurrency = useSelector(
    (state: RootStateType) => state.orgSetup.organizationData?.currency?.name || '',
  );

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'date', headerName: 'Date', width: 130, cellClassName: 'md-text', headerClassName: 'md-text' },
      {
        field: 'shipmentNo',
        headerName: 'Shipment No',
        width: 130,
        renderCell: (params: GridValueGetterParams) => (
          <Link to={`/shipment/admin/edit/${params?.value}`} className="sb-text-black underline">
            {params?.value}
          </Link>
        ),
        cellClassName: 'md-text',
        headerClassName: 'md-text',
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 160,
        cellClassName: 'md-text',
        headerClassName: 'md-text',
      },
      {
        field: 'shipper',
        headerName: 'Shipper',
        sortable: false,
        width: 180,
        renderCell: (params: GridValueGetterParams) => (
          <Box>
            <p className="font-medium capitalize">{params?.value?.name}</p>
            <p>
              {params?.value?.city}-{params?.value?.pincode}
            </p>
            <p>{params?.value?.country}</p>
          </Box>
        ),
        cellClassName: 'md-text',
        headerClassName: 'md-text',
      },
      {
        field: 'consignee',
        headerName: 'Consignee',
        sortable: false,
        width: 180,
        renderCell: (params: GridValueGetterParams) => (
          <Box>
            <p className="font-medium capitalize">{params?.value?.name}</p>
            <p>
              {params?.value?.city}-{params?.value?.pincode}
            </p>
            <p>{params?.value?.country}</p>
          </Box>
        ),
        cellClassName: 'md-text',
        headerClassName: 'md-text',
      },
      {
        field: 'weight',
        headerName: 'Weight',
        width: 160,
        cellClassName: 'md-text',
        headerClassName: 'md-text',
      },
      {
        field: 'packages',
        headerName: 'Packages',
        width: 160,
        cellClassName: 'md-text',
        headerClassName: 'md-text',
      },
      {
        field: 'totalAmount',
        headerName: 'Total Amount',
        width: 180,
        cellClassName: 'md-text',
        headerClassName: 'md-text',
      },
    ],
    [],
  );

  const rows = useMemo(
    () =>
      shipments.map((s, i: number) => {
        const rawAmt = getShipmentExtraChargeSum(s, 'credit');
        return {
          id: i + 1,
          date: moment(s.createdAt).format('DD/MM/YYYY'),
          shipmentNo: s.shipmentNumber,
          status: SHIPMENT_STATUS[s.status]?.label || s.status,
          shipper: s._shipperId,
          consignee: s._consigneeId,
          weight: s?.sellingPricing?.displayChargeableWeight ? `${s.sellingPricing.displayChargeableWeight}kg` : '-',
          packages: s?._packageId?.box?.length || 0,
          totalAmount: formatNumber(rawAmt, locale),
          isDisabled: Boolean(s.carrierDetails && s.carrierDetails?.awb),
        };
      }),
    [shipments, locale],
  );

  const rowSelectionModel = useMemo(() => {
    if (!selectedUnbilledRow?.length || !shipments?.length) return [];
    const indexById = new Map<string, number>();
    shipments.forEach((s, idx) => {
      if (s?._id) indexById.set(s._id, idx + 1);
    });
    return selectedUnbilledRow.flatMap(item =>
      (item?.shipments || []).map(ship => indexById.get(ship?._id) ?? -1).filter(id => id !== -1),
    );
  }, [selectedUnbilledRow, shipments]);

  const handleRowSelectionChange = useCallback(
    (selectedRows: GridRowSelectionModel) => {
      const selectedSet = new Set(selectedRows.map(Number));
      const shipmentRow = shipments.filter((_, i) => selectedSet.has(i + 1));
      const sysId =
        typeof _systemId === 'string'
          ? _systemId
          : _systemId?._id || system?._id || (typeof system === 'string' ? system : '');
      const obj: SelectedCreditDebitParamType = {
        accountNumber: account?.accountNumber || props.data.accountNumber || '',
        _systemId: sysId,
        shipments: shipmentRow,
        totalAmount: totalInvoiceAmount,
        systemName: system?.name || (typeof _systemId === 'object' ? _systemId?.name : '') || '',
        invoiceNumber,
        _userId: user?._id,
      };
      setSelectedUnbilledShipment(obj);
    },
    [
      shipments,
      _systemId,
      system,
      account,
      props.data.accountNumber,
      totalInvoiceAmount,
      invoiceNumber,
      user,
      setSelectedUnbilledShipment,
    ],
  );

  const summaryNode = (
    <Stack direction={'row'} justifyContent={'space-between'} pr={'1rem'}>
      <Stack gap={'10px'}>
        <Stack direction={'row'} gap={'2rem'}>
          <Box sx={{ minWidth: '5rem' }} className="md-text">
            {company ? company?.name : user ? user?.name : '-'}
          </Box>
          <p className="md-text has-text-grey-light">{company ? company?.email : user ? user?.email : '-'}</p>
        </Stack>
        <Stack direction={'row'} gap={'2rem'}>
          <p className="md-text font-semibold">{account?.accountNumber || props.data.accountNumber}</p>
          <p className="md-text">{system?.name || (typeof _systemId === 'object' ? _systemId?.name : '')}</p>
          <p className="md-text">Invoice No:{invoiceNumber}</p>
          <p className="md-text">Shipments({shipments?.length})</p>
        </Stack>
      </Stack>
      <Box
        sx={{
          justifySelf: 'flex-end',
          alignSelf: 'center',
          fontWeight: 600,
          color: 'primary.main',
        }}
        className="md-text"
      >
        Total Amount: {orgCountryCurrency} {formatNumber(totalInvoiceAmount, locale)}
      </Box>
    </Stack>
  );

  const detailsNode = (
    <DataGrid
      rows={rows}
      columns={columns}
      initialState={{
        pagination: {
          paginationModel: { page: 0, pageSize: 5 },
        },
      }}
      rowHeight={90}
      rowSelectionModel={rowSelectionModel}
      checkboxSelection
      onRowSelectionModelChange={handleRowSelectionChange}
    />
  );

  return <Accord summary={summaryNode} details={detailsNode} />;
}
